import type { IDataState } from "@/store/modules/data";
import { DataModules } from "@/store/modules/data/modules";
import i18n from "@/i18n";
import type { ActionTree, GetterTree } from "vuex";
import type { IState } from "@/store";
import type { ApiPathGetter } from "@/models/api";
import {
  ConfirmModalModes,
  type IClient,
  type IUser
} from "@upmind-automation/types";

const initialState = {} as IDataState;

const getters: GetterTree<IDataState, IState> = {
  apiPath:
    (): ApiPathGetter =>
    ({
      clientId,
      userId
    }: {
      clientId: IClient["id"];
      userId: IUser["id"];
    }) => {
      const admin = `api/admin/${
        clientId ? `clients/${clientId}/` : userId ? `users/${userId}/` : ""
      }auth_provider_configurations`;
      return { admin };
    },
  listScope:
    (): ApiPathGetter =>
    (suffix = "") => {
      return `$auth_configurations${suffix}`;
    },
  scope: (): ApiPathGetter => configId => {
    return `$auth_configuration_${configId}`;
  },
  fieldValuesScope: (): ApiPathGetter => configId => {
    return `$auth_configuration_${configId}_field_values`;
  }
};

const actions: ActionTree<IDataState, IState> = {
  list: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/list",
      {
        ...payload,
        path: getters.apiPath(payload).admin,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  },
  get: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/get",
      {
        ...payload,
        path: `${getters.apiPath(payload).admin}/${payload.id}`,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  },
  create: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/create",
      {
        ...payload,
        path: getters.apiPath(payload).admin,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  },
  duplicate: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/create",
      {
        path: `${getters.apiPath(payload).admin}/duplicate`,
        ...payload,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  },
  update: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/update",
      {
        ...payload,
        path: `${getters.apiPath(payload).admin}/${payload.id}`,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  },
  remove: async ({ dispatch }, { id, onSuccess, onError, onCancel }) => {
    const confirm = await dispatch(
      "ui/open/confirmModal",
      {
        config: {
          props: {
            autoClose: false,
            confirmButtonText: i18n.t("_action.confirm"),
            mode: ConfirmModalModes.PIN,
            message: i18n.t("_sentence.auth.confirm_provider_deletion")
          },
          canCancel: [],
          events: {
            close: onCancel,
            confirmed: async () => {
              try {
                await dispatch("hardDelete", { id });
                onSuccess();
              } catch (error) {
                if (typeof onError === "function") onError(error);
                dispatch("api/handleError", error, {
                  root: true
                });
              } finally {
                confirm.close();
              }
            }
          }
        }
      },
      { root: true }
    );
  },
  hardDelete: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/remove",
      {
        ...payload,
        path: `${getters.apiPath(payload).admin}/${
          payload.id
        }?confirm_delete=true`,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  },
  openManageConfigurationModal: ({ dispatch }, payload) => {
    return dispatch(
      "ui/open/slideModal",
      {
        config: {
          component: () =>
            import(
              "@/components/app/global/authConfigurations/authConfigurationManageModal.vue"
            ),
          ...payload
        }
      },
      { root: true }
    );
  },
  getFieldValues: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/list",
      {
        ...payload,
        path: `${getters.apiPath(payload).admin}/${
          payload.configId
        }/field_values`,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  },
  syncFieldValues: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/update",
      {
        ...payload,
        path: `${getters.apiPath(payload).admin}/${
          payload.configId
        }/field_values`,
        storeModule: DataModules.AUTH_CONFIGURATIONS
      },
      { root: true }
    );
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  modules: {
    actors: require("./actors").default,
    logs: require("./logs").default
  }
};
